import React, { useState } from 'react';
import { Box } from '@mui/material';

// Default fallback image
const DEFAULT_FALLBACK = 'https://placehold.co/600x400/e0e0e0/969696?text=Image+Not+Available';

/**
 * Enhanced Image component with error handling
 * @param {Object} props Component props
 * @param {string} props.src Image source URL
 * @param {string} [props.fallbackSrc] Optional fallback image source
 * @param {string} props.alt Alt text for the image
 * @param {Object} props.sx MUI sx styling properties
 * @param {string} [props.objectFit='cover'] Object-fit property
 * @param {string} [props.objectPosition='center'] Object-position property
 * @param {Function} [props.onLoad] Optional callback when image loads
 * @param {Function} [props.onError] Optional callback when image fails to load
 * @param {Object} [props.imgProps] Additional props for the img element
 */
const Image = ({
  src,
  fallbackSrc = DEFAULT_FALLBACK,
  alt,
  sx = {},
  objectFit = 'cover',
  objectPosition = 'center',
  onLoad,
  onError,
  imgProps = {},
  ...rest
}) => {
  const [imageError, setImageError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const handleImageError = () => {
    setImageError(true);
    if (onError) onError();
  };

  const handleImageLoad = () => {
    setLoaded(true);
    if (onLoad) onLoad();
  };

  if (imageError) {
    return (
      <Box
        component="img"
        src={fallbackSrc}
        alt={alt || 'Image not available'}
        sx={{
          width: '100%',
          height: '100%',
          objectFit,
          objectPosition,
          ...sx
        }}
        {...rest}
      />
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        ...sx
      }}
      {...rest}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        onError={handleImageError}
        onLoad={handleImageLoad}
        sx={{
          width: '100%',
          height: '100%',
          objectFit,
          objectPosition,
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
        {...imgProps}
      />
    </Box>
  );
};

export default Image; 